import { memo, useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Card, Button, Select, DatePicker, Space, Tooltip, Modal } from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  DownloadOutlined,
  ExpandOutlined,
  CompressOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import GeocollectMembersSelector from '~/components/app_common/GeocollectMembersSelector/GeocollectMembersSelector';
import { useAtomValue } from 'jotai';
import { projectMembersWithRolesAtom } from '~/components/Map/Member/MemberStore';
import { HiOutlineChartBar, HiOutlineUsers, HiOutlineEye, HiOutlineFilter } from 'react-icons/hi';
import { FaTriangleExclamation } from 'react-icons/fa6';
import { PiTrendUp, PiChartBar, PiTrendDown } from 'react-icons/pi';

import { CollectDashboardData, CollectTimeRange } from '~/common/typescript/models/CollectDashboard';
import { CollectDashboardAPIs } from '~/common/typescript/apis/CollectDashboardAPIs';

interface DashboardTabProps {
  tableHeight: string;
  onMaximize: () => void;
  onClose: () => void;
  isMaximized: boolean;
  projectId?: number;
  onFormSelect?: (formUuid: string, formData?: any) => void;
}

const DashboardTab = ({ tableHeight, onMaximize, onClose, isMaximized, projectId, onFormSelect }: DashboardTabProps) => {
  const memberWithRoles = useAtomValue(projectMembersWithRolesAtom);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedForm, setSelectedForm] = useState<string>('all');
  const [selectedCollector, setSelectedCollector] = useState<number>(-1); // Initialize with -1 for "All"
  const [timeRange, setTimeRange] = useState<CollectTimeRange>('all');
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [drillDownView, setDrillDownView] = useState<{ type: 'form' | 'collector' | null; id: string | number | null }>(
    { type: null, id: null },
  );

  const [dashboardData, setDashboardData] = useState<CollectDashboardData>({
    overview: {
      totalRecords: 0,
      completionRate: 0,
      errorRate: -1,
      trends: {
        totalRecords: [],
        completionRate: [],
        errorRate: [],
      },
    },
    formsProgress: [],
    staffPerformance: [],
    errorHeatmap: {
      forms: [],
      fields: [],
      data: [],
    },
    recentErrors: [],
  });
  const [loading, setLoading] = useState(true);
  const [nameLookup, setNameLookup] = useState<Record<number, string>>({});
  const [customDateRange, setCustomDateRange] = useState<[string, string] | null>(null); // [startDate, endDate]

  // Fetch dashboard overview data on component mount and when filters change
  useEffect(() => {
    const fetchDashboardData = async () => {
      // Only fetch if projectId is available
      if (projectId === undefined || projectId === null) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Create filters object based on current state
        const filters: Partial<CollectDashboardFilters> = {
          formId: selectedForm !== 'all' ? selectedForm : undefined,
          collectorId: selectedCollector !== -1 ? selectedCollector.toString() : undefined,
          projectId: projectId,
        };

        // Only add timeRange to filters if it's not 'all'
        if (timeRange !== 'all') {
          filters.timeRange = timeRange;
        }

        // Fetch all dashboard data in parallel using the combined endpoint
        const data = await CollectDashboardAPIs.remote_getCollectDashboardData(filters);
        setDashboardData(data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedForm, selectedCollector, timeRange, projectId]);

  // Create name lookup map when members change
  useEffect(() => {
    const lookup: Record<number, string> = {};
    memberWithRoles.forEach((m: any) => {
      if (m.user?.id) {
        lookup[m.user.id] = m.user.fullname || m.user.username || `User ${m.user.id}`;
      }
    });
    setNameLookup(lookup);
  }, [memberWithRoles]);

  const handleRefresh = async () => {
    // Only refresh if projectId is available
    if (projectId === undefined || projectId === null) {
      setRefreshing(false);
      return;
    }

    setRefreshing(true);
    try {
      // Refresh all dashboard data
      await CollectDashboardAPIs.remote_refreshCollectDashboardData();

      // Then fetch the updated data with current filters
      const filters: Partial<CollectDashboardFilters> = {
        formId: selectedForm !== 'all' ? selectedForm : undefined,
        collectorId: selectedCollector !== -1 ? selectedCollector.toString() : undefined,
        projectId: projectId,
      };

      // Handle time range filtering
      if (timeRange === 'custom' && customDateRange) {
        // Use custom date range
        filters.startDate = customDateRange[0];
        filters.endDate = customDateRange[1];
      } else if (timeRange !== 'all') {
        // Use predefined time range
        filters.timeRange = timeRange;
      }

      // Fetch the complete dashboard data with the filters
      const updatedData = await CollectDashboardAPIs.remote_getCollectDashboardData(filters);

      // Ensure error rates are properly handled - no default value for -1 since that's a valid value
      setDashboardData({
        ...updatedData,
        overview: {
          ...updatedData.overview,
          // No need to default errorRate to 0 since -1 is a valid value indicating no data
        },
      });
    } catch (error) {
      console.error('Error refreshing dashboard data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Set up automatic refresh polling every 5 minutes
  useEffect(() => {
    if (projectId === undefined || projectId === null) {
      return; // Don't start polling if no projectId is available
    }

    // Set up interval to refresh data every 5 minutes (300000 ms)
    const intervalId = setInterval(
      async () => {
        try {
          // Create filters object based on current state
          const filters: Partial<CollectDashboardFilters> = {
            formId: selectedForm !== 'all' ? selectedForm : undefined,
            collectorId: selectedCollector !== -1 ? selectedCollector.toString() : undefined,
            projectId: projectId,
          };

          // Only add timeRange to filters if it's not 'all'
          if (timeRange !== 'all') {
            filters.timeRange = timeRange;
          }

          // Fetch the complete dashboard data with the filters
          const updatedData = await CollectDashboardAPIs.remote_getCollectDashboardData(filters);

          // Update the dashboard data with the fetched data
          setDashboardData(updatedData);
        } catch (error) {
          console.error('Error during automatic refresh:', error);
        }
      },
      5 * 60 * 1000,
    ); // 5 minutes in milliseconds

    // Clean up the interval when the component unmounts or dependencies change
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [projectId, selectedForm, selectedCollector, timeRange]);

  const getStatusColor = (value: number, type: string) => {
    // Return gray color if value is -1 (no data available)
    if (value === -1) return '#94a3b8'; // gray

    switch (type) {
      case 'completion':
        if (value >= 80) return '#22c55e'; // green
        if (value >= 50) return '#f59e0b'; // yellow
        return '#ef4444'; // red
      case 'error':
        if (value <= 3) return '#22c55e'; // green
        if (value <= 5) return '#f59e0b'; // yellow
        return '#ef4444'; // red
      default:
        return '#3b82f6'; // blue
    }
  };

  const getTrendIcon = (trend: string) => {
    return trend === 'up' ? '↑' : '↓';
  };

  // Function to handle form click for drill-down
  const handleFormClick = (formName: string) => {
    // Set drill-down view to form level
    setDrillDownView({ type: 'form', id: formName });

    // Update the form filter to show only the selected form
    setSelectedForm(formName);
  };

  // Function to handle collector click for drill-down
  const handleCollectorClick = (collectorId: string | number) => {
    // Set drill-down view to collector level
    setDrillDownView({ type: 'collector', id: collectorId });

    // Update the collector filter to show only the selected collector
    setSelectedCollector(collectorId.toString());
  };

  // Function to reset drill-down view
  const handleResetDrillDown = () => {
    setDrillDownView({ type: null, id: null });
    setSelectedForm('all');
    setSelectedCollector('all');
  };

  const FilterContent = () => (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <UI_FilterGroup>
        <UI_FilterLabel>Biểu mẫu:</UI_FilterLabel>
        <Select
          value={selectedForm}
          onChange={setSelectedForm}
          size="small"
          style={{ width: '100%' }}
          options={[
            { label: 'Tất cả', value: 'all' },
            { label: 'Khảo sát CSKH', value: 'survey' },
            { label: 'Kiểm tra kho', value: 'warehouse' },
            { label: 'Thu thập hiện trường', value: 'field' },
          ]}
        />
      </UI_FilterGroup>

      <UI_FilterGroup>
        <UI_FilterLabel>Nhân viên:</UI_FilterLabel>
        <GeocollectMembersSelector
          showAllOption={true}
          allOptionLabel="Tất cả"
          value={selectedCollector}
          onChange={setSelectedCollector}
          style={{ width: '100%' }}
          placeholder="Chọn nhân viên..."
        />
      </UI_FilterGroup>

      <UI_FilterGroup>
        <UI_FilterLabel>Thời gian:</UI_FilterLabel>
        <Select
          value={timeRange}
          onChange={setTimeRange}
          size="small"
          style={{ width: '100%' }}
          options={[
            { label: 'Tất cả', value: 'all' },
            { label: 'Hôm nay', value: 'today' },
            { label: '3 ngày', value: '3days' },
            { label: '7 ngày', value: '7days' },
            { label: 'Tháng này', value: 'this_month' },
            { label: 'Tùy chỉnh', value: 'custom' },
          ]}
        />
      </UI_FilterGroup>
    </Space>
  );

  return (
    <UI_Container>
      {/* Header with controls */}
      <UI_Header>
        <UI_HeaderLeft>
          <UI_Title>
            Dashboard
            {drillDownView.type && (
              <span style={{ marginLeft: '10px', fontSize: '12px', color: '#64748b' }}>
                → {drillDownView.type === 'form' ? `Form: ${drillDownView.id}` : `Collector: ${drillDownView.id}`}
              </span>
            )}
          </UI_Title>
        </UI_HeaderLeft>
        <UI_HeaderRight>
          <Space size="small">
            {drillDownView.type && (
              <Tooltip title="Reset view">
                <Button icon={<HiOutlineEye />} size="small" onClick={handleResetDrillDown} type="text" />
              </Tooltip>
            )}
            <UI_MobileFilterButton>
              <Tooltip title="Bộ lọc">
                <Button icon={<HiOutlineFilter />} size="small" onClick={() => setShowFiltersModal(true)} type="text" />
              </Tooltip>
            </UI_MobileFilterButton>
            <Tooltip title="Tải xuống báo cáo">
              <Button icon={<DownloadOutlined />} size="small" type="text" />
            </Tooltip>
            <Tooltip title="Làm mới">
              <Button icon={<ReloadOutlined />} size="small" onClick={handleRefresh} loading={refreshing} type="text" />
            </Tooltip>
            <Tooltip title={isMaximized ? 'Thu nhỏ' : 'Phóng to'}>
              <Button
                icon={isMaximized ? <CompressOutlined /> : <ExpandOutlined />}
                size="small"
                onClick={onMaximize}
                type="text"
              />
            </Tooltip>
            <Tooltip title="Đóng">
              <Button icon={<CloseOutlined />} size="small" onClick={onClose} type="text" />
            </Tooltip>
          </Space>
        </UI_HeaderRight>
      </UI_Header>

      {/* Quick Filters - Desktop only */}
      <UI_Filters>
        <Space size="middle" wrap>
          <UI_FilterGroup>
            <UI_FilterLabel>Biểu mẫu:</UI_FilterLabel>
            <Select
              value={selectedForm}
              onChange={setSelectedForm}
              size="small"
              style={{ minWidth: 120 }}
              options={[
                { label: 'Tất cả', value: 'all' },
                { label: 'Khảo sát CSKH', value: 'survey' },
                { label: 'Kiểm tra kho', value: 'warehouse' },
                { label: 'Thu thập hiện trường', value: 'field' },
              ]}
            />
          </UI_FilterGroup>

          <UI_FilterGroup>
            <UI_FilterLabel>Nhân viên:</UI_FilterLabel>
            <GeocollectMembersSelector
              showAllOption={true}
              allOptionLabel="Tất cả"
              value={selectedCollector}
              onChange={setSelectedCollector}
              style={{ minWidth: 120 }}
              placeholder="Chọn nhân viên..."
            />
          </UI_FilterGroup>

          <UI_FilterGroup>
            <UI_FilterLabel>Thời gian:</UI_FilterLabel>
            <Select
              value={timeRange}
              onChange={setTimeRange}
              size="small"
              style={{ minWidth: 120 }}
              options={[
                { label: 'Tất cả', value: 'all' },
                { label: 'Hôm nay', value: 'today' },
                { label: '3 ngày', value: '3days' },
                { label: '7 ngày', value: '7days' },
                { label: 'Tháng này', value: 'this_month' },
                { label: 'Tùy chỉnh', value: 'custom' },
              ]}
            />
          </UI_FilterGroup>
        </Space>
      </UI_Filters>

      {/* Mobile Filters Modal */}
      <Modal
        title="Bộ lọc"
        open={showFiltersModal}
        onCancel={() => setShowFiltersModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowFiltersModal(false)}>
            Hủy
          </Button>,
          <Button key="apply" type="primary" onClick={() => setShowFiltersModal(false)}>
            Áp dụng
          </Button>,
        ]}
        width={320}
      >
        <FilterContent />
      </Modal>

      {/* Dashboard Content */}
      <UI_DashboardContent style={{ height: tableHeight }}>
        <UI_TopCardsContainer>
          {/* KPI Overview */}
          <UI_KPICard>
            <UI_CardTitle>
              <HiOutlineChartBar />
              Tổng quan
            </UI_CardTitle>
            <UI_KPIGrid>
              <UI_KPIItem>
                <UI_KPIValue>{dashboardData.overview.totalRecords.toLocaleString()}</UI_KPIValue>
                <UI_KPILabel>Bản ghi thu thập</UI_KPILabel>
                <UI_Sparkline><PiTrendUp style={{ fontSize: 18, color: '#1677ff' }} /></UI_Sparkline>
              </UI_KPIItem>
              <UI_KPIItem>
                <UI_KPIValue style={{ color: getStatusColor(dashboardData.overview.completionRate, 'completion') }}>
                  {dashboardData.overview.completionRate === -1 ? '-' : `${dashboardData.overview.completionRate}%`}
                </UI_KPIValue>
                <UI_KPILabel>Tỷ lệ hoàn thành</UI_KPILabel>
                <UI_Sparkline><PiChartBar style={{ fontSize: 18, color: '#52c41a' }} /></UI_Sparkline>
              </UI_KPIItem>
              <UI_KPIItem>
                <UI_KPIValue style={{ color: getStatusColor(dashboardData.overview.errorRate, 'error') }}>
                  {dashboardData.overview.errorRate === -1 ? '-' : `${dashboardData.overview.errorRate}%`}
                </UI_KPIValue>
                <UI_KPILabel>Tỷ lệ lỗi</UI_KPILabel>
                <UI_Sparkline><PiTrendDown style={{ fontSize: 18, color: '#f5222d' }} /></UI_Sparkline>
              </UI_KPIItem>
            </UI_KPIGrid>
          </UI_KPICard>

          {/* Progress by Form */}
          <UI_ProgressCard>
            <UI_CardTitle>
              <HiOutlineChartBar />
              Tiến độ theo Biểu mẫu
            </UI_CardTitle>
            <UI_ProgressList>
              {dashboardData.formsProgress.map((form, index) => (
                <UI_ProgressItem 
                  key={index} 
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    if (onFormSelect && form.formUuid) {
                      onFormSelect(form.formUuid, form);
                    }
                  }}
                >
                  <UI_ProgressInfo>
                    <UI_ProgressName>{form.name}</UI_ProgressName>
                    <UI_ProgressStats>
                      {form.target === -1
                        ? `${form.current} (chưa đặt KPI)`
                        : `${form.percentage}% (${form.current}/${form.target})`}
                    </UI_ProgressStats>
                  </UI_ProgressInfo>
                  {form.target !== -1 && (
                    <UI_ProgressBar>
                      <UI_ProgressFill
                        percentage={form.percentage}
                        color={getStatusColor(form.percentage, 'completion')}
                      />
                    </UI_ProgressBar>
                  )}
                </UI_ProgressItem>
              ))}
            </UI_ProgressList>
          </UI_ProgressCard>

          {/* Staff Performance */}
          <UI_StaffCard>
            <UI_CardTitle>
              <HiOutlineUsers />
              Top Nhân viên
            </UI_CardTitle>
            <UI_StaffTable>
              <UI_StaffHeader>
                <span>Rank</span>
                <span>Tên</span>
                <span>Bản ghi</span>
                {/* <span>HT%</span>
                <span>Lỗi%</span> */}
              </UI_StaffHeader>
              {dashboardData.staffPerformance.slice(0, 10).map((staff, index) => {
                // Extract user ID from staff record to look up real name
                const userId = staff.userId || parseInt(staff.name?.replace('User ', '') || '0');
                const displayName = nameLookup[userId] || staff.name;

                return (
                  <UI_StaffRow
                    key={staff.rank}
                    hasError={staff.errors !== -1 && (staff.errors > 5 || staff.completion < 70)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span>#{index + 1}</span>
                    <span>{displayName}</span>
                    <span>{staff.records.toLocaleString()}</span>
                    {/* <span style={{ color: getStatusColor(staff.completion, 'completion') }}>
                      {staff.completion === -1 ? '-' : `${staff.completion}%`} {getTrendIcon(staff.trend)}
                    </span>
                    <span style={{ color: getStatusColor(staff.errors, 'error') }}>
                      {staff.errors === -1 ? '-' : `${staff.errors}%`}
                    </span> */}
                  </UI_StaffRow>
                );
              })}
            </UI_StaffTable>
          </UI_StaffCard>
        </UI_TopCardsContainer>

        {/* Bottom Row */}
        <UI_BottomCardsContainer style={{ display: 'none' }}>
          {/* Error Heatmap */}
          <UI_HeatmapCard>
            <UI_CardTitle>
              <FaTriangleExclamation />
              Lỗi theo Biểu mẫu × Field
            </UI_CardTitle>
            <UI_Heatmap>
              <UI_HeatmapTable>
                <thead>
                  <tr>
                    <th></th>
                    {dashboardData.errorHeatmap.fields.map((field) => (
                      <th key={field}>{field}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.errorHeatmap.forms.map((form, i) => (
                    <tr key={form}>
                      <td onClick={() => handleFormClick(form)} style={{ cursor: 'pointer' }}>
                        {form}
                      </td>
                      {dashboardData.errorHeatmap.data[i].map((value, j) => (
                        <td key={j}>
                          <UI_HeatmapCell intensity={value}>{value === -1 ? '-' : `${value}%`}</UI_HeatmapCell>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </UI_HeatmapTable>
            </UI_Heatmap>
          </UI_HeatmapCard>

          {/* Recent Errors */}
          <UI_ErrorsCard>
            <UI_CardTitle>
              <FaTriangleExclamation />
              Lỗi gần đây
            </UI_CardTitle>
            <UI_ErrorsList>
              {dashboardData.recentErrors.map((error, index) => {
                // Extract user ID from error record to look up real name
                const userId = error.userId || parseInt(error.collector?.replace('User ', '') || '0');
                const displayName = nameLookup[userId] || error.collector;

                return (
                  <UI_ErrorItem key={index}>
                    <UI_ErrorTime>{error.time}</UI_ErrorTime>
                    <UI_ErrorDetails>
                      <UI_ErrorUser onClick={() => handleCollectorClick(displayName)} style={{ cursor: 'pointer' }}>
                        {displayName}
                      </UI_ErrorUser>
                      <UI_ErrorForm onClick={() => handleFormClick(error.form)} style={{ cursor: 'pointer' }}>
                        {error.form} → {error.field}
                      </UI_ErrorForm>
                      <UI_ErrorNote>{error.note}</UI_ErrorNote>
                    </UI_ErrorDetails>
                  </UI_ErrorItem>
                );
              })}
            </UI_ErrorsList>
          </UI_ErrorsCard>
        </UI_BottomCardsContainer>
      </UI_DashboardContent>
    </UI_Container>
  );
};

export default memo(DashboardTab);

// Styled Components
const UI_Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f8fafc;
`;

const UI_Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px 16px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
`;

const UI_HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const UI_HeaderRight = styled.div``;

const UI_Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
`;

const UI_Subtitle = styled.p`
  margin: 0;
  font-size: 12px;
  color: #64748b;
`;

const UI_Filters = styled.div`
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #e2e8f0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const UI_FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const UI_FilterLabel = styled.span`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  min-width: 60px;
`;

const UI_DashboardContent = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
`;

const UI_CardTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const UI_KPICard = styled(Card)`
  height: 400px;
  flex: 1;
  min-width: 300px;
  overflow: hidden;
  .ant-card-body {
    padding: 16px;
    height: calc(100% - 48px);
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }
`;

const UI_KPIGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  flex: 1;
  min-height: 0;
  justify-content: center;
  align-items: flex-start;
`;

const UI_KPIItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 12px;
  border-radius: 8px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  width: 150px;
  flex: 0 0 auto;
`;

const UI_KPIValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #3b82f6;
  margin-bottom: 4px;
`;

const UI_KPILabel = styled.div`
  font-size: 11px;
  color: #64748b;
  margin-bottom: 4px;
`;

const UI_Sparkline = styled.div`
  font-size: 12px;
`;

const UI_ProgressCard = styled(Card)`
  height: 400px;
  flex: 1;
  min-width: 300px;
  overflow: hidden;
  .ant-card-body {
    padding: 16px;
    height: calc(100% - 48px);
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }
`;

const UI_ProgressList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const UI_ProgressItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const UI_ProgressInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const UI_ProgressName = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #374151;
`;

const UI_ProgressStats = styled.span`
  font-size: 11px;
  color: #64748b;
`;

const UI_ProgressBar = styled.div`
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
`;

const UI_ProgressFill = styled.div<{ percentage: number; color: string }>`
  height: 100%;
  width: ${(props) => props.percentage}%;
  background: ${(props) => props.color};
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const UI_StaffCard = styled(Card)`
  height: 400px;
  flex: 1;
  min-width: 300px;
  overflow: hidden;
  .ant-card-body {
    padding: 16px;
    height: calc(100% - 48px);
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }
`;

const UI_StaffTable = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  font-size: 11px;
`;

const UI_StaffHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 60px 50px 50px;
  gap: 8px;
  padding: 6px 0;
  font-weight: 600;
  color: #64748b;
  border-bottom: 1px solid #e5e7eb;
`;

const UI_StaffRow = styled.div<{ hasError?: boolean }>`
  display: grid;
  grid-template-columns: 40px 1fr 60px 50px 50px;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #f3f4f6;
  color: ${(props) => (props.hasError ? '#ef4444' : '#374151')};
  background: ${(props) => (props.hasError ? 'rgba(239, 68, 68, 0.05)' : 'transparent')};
  border-radius: 4px;
  margin: 2px 0;
`;

const UI_HeatmapCard = styled(Card)`
  height: 480px;
  flex: 2;
  min-width: 280px;
  max-width: 100%;
  overflow: hidden;
  .ant-card-body {
    padding: 16px;
    height: calc(100% - 48px);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  @media (max-width: 768px) {
    min-width: 100%;
    max-width: 100%;
  }
`;

const UI_Heatmap = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
  max-width: 100%;
`;

const UI_HeatmapTable = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 11px;

  th,
  td {
    padding: 4px 8px;
    text-align: center;
    border: 1px solid #e5e7eb;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  th {
    background: #f8fafc;
    font-weight: 600;
    color: #64748b;
  }

  td:first-child,
  th:first-child {
    text-align: left;
    font-weight: 500;
    background: #f8fafc;
    color: #374151;
    width: 40%;
  }

  td:not(:first-child),
  th:not(:first-child) {
    width: 30%;
  }

  @media (max-width: 768px) {
    font-size: 10px;

    th,
    td {
      padding: 2px 4px;
    }

    td:first-child,
    th:first-child {
      width: 50%;
    }

    td:not(:first-child),
    th:not(:first-child) {
      width: 25%;
    }
  }
`;

const UI_HeatmapCell = styled.div<{ intensity: number }>`
  background: ${(props) => {
    // If intensity is -1 (no data), use a light gray background
    if (props.intensity === -1) {
      return 'rgba(148, 163, 184, 0.2)'; // light gray
    }
    // Otherwise, use the normal intensity calculation
    const opacity = Math.min(props.intensity / 20, 1);
    return `rgba(239, 68, 68, ${opacity})`;
  }};
  color: ${(props) => (props.intensity === -1 ? '#64748b' : props.intensity > 10 ? 'white' : '#374151')};
  padding: 2px 4px;
  border-radius: 2px;
  font-weight: 500;
`;

const UI_ErrorsCard = styled(Card)`
  height: 480px;
  flex: 1;
  min-width: 300px;
  overflow: hidden;
  .ant-card-body {
    padding: 16px;
    height: calc(100% - 48px);
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }
`;

const UI_ErrorsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const UI_ErrorItem = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  max-width: 100%;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 4px;
    padding: 6px;
  }
`;

const UI_ErrorTime = styled.div`
  font-size: 10px;
  color: #b91c1c;
  font-weight: 500;
  min-width: 50px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    min-width: auto;
    font-size: 9px;
  }
`;

const UI_ErrorDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
`;

const UI_ErrorUser = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const UI_ErrorForm = styled.div`
  font-size: 10px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 9px;
  }
`;

const UI_ErrorNote = styled.div`
  font-size: 10px;
  color: #dc2626;
  font-style: italic;
  word-wrap: break-word;
  overflow-wrap: break-word;

  @media (max-width: 768px) {
    font-size: 9px;
  }
`;

const UI_TopCardsContainer = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const UI_BottomCardsContainer = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const UI_MobileFilterButton = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;
